<template>
  <div class="bg-white border border-gray-200 rounded-lg mt-4">
    <!-- Header -->
    <div class="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
      <h3 class="text-sm font-semibold text-gray-900">Cost Attribution Flow</h3>
      <div class="flex items-center gap-3">
        <!-- Contract picker (shown when >1 contract has BEUR data) -->
        <select
          v-if="availableContracts.length > 1"
          v-model="selectedContractId"
          class="text-xs border border-gray-300 rounded px-2 py-1"
          @change="onContractChange"
        >
          <option v-for="c in availableContracts" :key="c.contract_id" :value="c.contract_id">
            {{ c.contract_name && c.contract_name !== c.contract_id ? `${c.contract_name} (${c.contract_id})` : c.contract_id }}
          </option>
        </select>
        <!-- Group by toggle -->
        <select v-model="groupBy" class="text-xs border border-gray-300 rounded px-2 py-1" @change="selectedTin = null; patients = []; fetchData()">
          <option value="tin">By Practice (TIN)</option>
          <option value="facility">By Facility</option>
          <option value="staff">By Staff (NPI)</option>
        </select>
        <!-- Risk adjustment toggle -->
        <label class="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none">
          <input type="checkbox" v-model="riskAdjusted" class="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5" />
          Risk-Adjusted
        </label>
        <!-- Report date selectors (from / to) -->
        <template v-if="reportPairs.length > 1">
          <select
            v-model="selectedDateA"
            class="text-xs border border-gray-300 rounded px-2 py-1"
            @change="onDateAChange"
          >
            <option v-for="d in dateAOptions" :key="d" :value="d">{{ d }}</option>
          </select>
          <span class="text-xs text-gray-400">→</span>
          <select
            v-model="selectedDateB"
            class="text-xs border border-gray-300 rounded px-2 py-1"
            @change="onDateBChange"
          >
            <option v-for="d in dateBOptions" :key="d" :value="d">{{ d }}</option>
          </select>
        </template>
      </div>
    </div>

    <div v-if="loading" class="p-8 text-center text-gray-400 text-sm">Loading...</div>

    <template v-else-if="summary">
      <!-- Panel summary cards -->
      <div class="grid grid-cols-6 gap-3 p-4">
        <div class="border border-gray-100 rounded-lg p-3">
          <div class="text-xs text-gray-500 mb-1">Panel A (From)</div>
          <div class="text-lg font-semibold">{{ fmt(panelA.patients) }}</div>
          <div class="text-xs text-gray-400">{{ fmtPY(panelA.py) }} PY · {{ fmtDollar(panelA.costPerPY) }}/PY</div>
          <div v-if="riskAdjusted" class="text-xs text-gray-400">Risk {{ panelA.risk?.toFixed(2) || '—' }}</div>
        </div>
        <div class="border border-gray-100 rounded-lg p-3">
          <div class="text-xs text-gray-500 mb-1">Panel B (To)</div>
          <div class="text-lg font-semibold">{{ fmt(panelB.patients) }}</div>
          <div class="text-xs text-gray-400">{{ fmtPY(panelB.py) }} PY · {{ fmtDollar(panelB.costPerPY) }}/PY</div>
          <div v-if="riskAdjusted" class="text-xs text-gray-400">Risk {{ panelB.risk?.toFixed(2) || '—' }}</div>
        </div>
        <div class="border border-gray-100 rounded-lg p-3">
          <div class="text-xs text-gray-500 mb-1">Remove Leavers</div>
          <div class="text-lg font-semibold" :class="leaverEffect < 0 ? 'text-emerald-600' : 'text-rose-600'">
            {{ fmtDollar(leaverEffect) }}/PY
          </div>
          <div class="text-xs text-gray-400">Leavers were {{ fmtDollar(summary.Leavers?.costPerPY) }}/PY</div>
        </div>
        <div class="border border-gray-100 rounded-lg p-3">
          <div class="text-xs text-gray-500 mb-1">Stayer Drift</div>
          <div class="text-lg font-semibold" :class="stayerDrift < 0 ? 'text-emerald-600' : stayerDrift > 0 ? 'text-rose-600' : 'text-gray-600'">
            {{ fmtDollar(stayerDrift) }}/PY
          </div>
          <div class="text-xs text-gray-400">{{ fmtDollar(summary.Stayers?.priorCostPerPY) }} → {{ fmtDollar(summary.Stayers?.costPerPY) }}</div>
        </div>
        <div class="border border-gray-100 rounded-lg p-3">
          <div class="text-xs text-gray-500 mb-1">Add Joiners</div>
          <div class="text-lg font-semibold" :class="joinerEffect > 0 ? 'text-rose-600' : 'text-emerald-600'">
            {{ fmtDollar(joinerEffect) }}/PY
          </div>
          <div class="text-xs text-gray-400">Joiners are {{ fmtDollar(summary.Joiners?.costPerPY) }}/PY</div>
        </div>
        <div class="border border-gray-100 rounded-lg p-3">
          <div class="text-xs text-gray-500 mb-1">= Cost Change</div>
          <div class="text-lg font-semibold" :class="costDelta < 0 ? 'text-emerald-600' : 'text-rose-600'">
            {{ fmtDollar(costDelta) }}/PY
          </div>
          <div class="text-xs text-gray-400">{{ fmtPct(costDeltaPct) }}</div>
        </div>
      </div>

      <!-- Leavers / Stayers / Joiners summary -->
      <div class="grid grid-cols-3 gap-3 px-4 pb-4">
        <div v-for="cat in ['Leavers', 'Stayers', 'Joiners']" :key="cat"
             class="border rounded-lg p-3"
             :class="cat === 'Leavers' ? 'border-rose-200 bg-rose-50' : cat === 'Joiners' ? 'border-blue-200 bg-blue-50' : 'border-gray-200'">
          <div class="text-xs font-medium mb-2" :class="cat === 'Leavers' ? 'text-rose-700' : cat === 'Joiners' ? 'text-blue-700' : 'text-gray-700'">
            {{ cat }}
          </div>
          <div class="text-sm font-semibold">{{ fmt(summary[cat]?.patients || 0) }} patients</div>
          <!-- Stayers: show before / after / drift -->
          <template v-if="cat === 'Stayers'">
            <div class="grid grid-cols-3 gap-2 mt-2 text-xs">
              <div>
                <div class="text-gray-400">Before</div>
                <div class="text-gray-700 font-medium">{{ fmtDollar(summary.Stayers?.priorCostPerPY) }}</div>
                <div class="text-gray-400 text-[10px]">/PY</div>
              </div>
              <div>
                <div class="text-gray-400">After</div>
                <div class="text-gray-700 font-medium">{{ fmtDollar(summary.Stayers?.costPerPY) }}</div>
                <div class="text-gray-400 text-[10px]">/PY</div>
              </div>
              <div>
                <div class="text-gray-400">Δ</div>
                <div class="font-medium"
                     :class="stayerDrift < 0 ? 'text-emerald-600' : stayerDrift > 0 ? 'text-rose-600' : 'text-gray-700'">
                  {{ fmtDollar(stayerDrift) }}
                </div>
                <div class="text-gray-400 text-[10px]">{{ fmtPct(stayerDriftPct) }}</div>
              </div>
            </div>
            <div v-if="riskAdjusted" class="text-xs text-gray-500 mt-1">
              Risk {{ (summary.Stayers?.priorRisk || 0).toFixed(2) }} → {{ (summary.Stayers?.risk || 0).toFixed(2) }}
            </div>
          </template>
          <!-- Leavers / Joiners: single cost point -->
          <template v-else>
            <div class="text-xs text-gray-500 mt-1">
              {{ fmtDollar(summary[cat]?.costPerPY) }}/PY · Risk {{ (summary[cat]?.risk || 0).toFixed(2) }}
            </div>
            <div v-if="summary[cat]?.deceased" class="text-xs text-gray-400 mt-1">
              {{ fmt(summary[cat].deceased) }} deceased ({{ fmtPct(summary[cat].deceased / summary[cat].patients) }})
            </div>
          </template>
        </div>
      </div>

      <!-- Practice-level table -->
      <div class="px-4 pb-4">
        <div class="text-xs font-semibold text-gray-700 mb-2">{{ groupByLabel }}</div>
        <div class="max-h-[400px] overflow-y-auto border border-gray-200 rounded">
          <table class="w-full text-xs">
            <thead class="bg-gray-50 sticky top-0">
              <tr>
                <th class="text-left px-3 py-2 font-medium text-gray-600" rowspan="2">{{ groupByColumnLabel }}</th>
                <th class="text-center px-3 py-2 font-medium text-rose-700 border-b border-gray-200" colspan="2">Leavers</th>
                <th class="text-center px-3 py-2 font-medium text-gray-700 border-b border-gray-200" colspan="4">Stayers</th>
                <th class="text-center px-3 py-2 font-medium text-blue-700 border-b border-gray-200" colspan="2">Joiners</th>
                <th class="text-right px-3 py-2 font-medium text-gray-600 cursor-pointer" rowspan="2" @click="sortBy('netSpend')">Δ Benchmark</th>
              </tr>
              <tr>
                <th class="text-right px-3 py-2 font-medium text-gray-400 cursor-pointer" @click="sortBy('leftPts')">Pts</th>
                <th class="text-right px-3 py-2 font-medium text-gray-400 cursor-pointer" @click="sortBy('leftCost')">$/PY</th>
                <th class="text-right px-3 py-2 font-medium text-gray-400 cursor-pointer" @click="sortBy('stayPts')">Pts</th>
                <th class="text-right px-3 py-2 font-medium text-gray-400 cursor-pointer" @click="sortBy('stayBeforeCost')">Before $/PY</th>
                <th class="text-right px-3 py-2 font-medium text-gray-400 cursor-pointer" @click="sortBy('stayAfterCost')">After $/PY</th>
                <th class="text-right px-3 py-2 font-medium text-gray-400 cursor-pointer" @click="sortBy('stayDelta')">Δ $/PY</th>
                <th class="text-right px-3 py-2 font-medium text-gray-400 cursor-pointer" @click="sortBy('joinPts')">Pts</th>
                <th class="text-right px-3 py-2 font-medium text-gray-400 cursor-pointer" @click="sortBy('joinCost')">$/PY</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in sortedPractices" :key="row.tin"
                  class="border-t border-gray-100 cursor-pointer"
                  :class="selectedTin === row.tin ? 'bg-blue-50 hover:bg-blue-100' : 'hover:bg-gray-50'"
                  @click="selectPractice(row)">
                <td class="px-3 py-2 text-gray-900">{{ row.name }}</td>
                <td class="px-3 py-2 text-right text-rose-600">{{ row.leftPts || '—' }}</td>
                <td class="px-3 py-2 text-right">{{ row.leftCost ? fmtDollar(row.leftCost) : '—' }}</td>
                <td class="px-3 py-2 text-right">{{ row.stayPts || '—' }}</td>
                <td class="px-3 py-2 text-right">{{ row.stayBeforeCost ? fmtDollar(row.stayBeforeCost) : '—' }}</td>
                <td class="px-3 py-2 text-right">{{ row.stayAfterCost ? fmtDollar(row.stayAfterCost) : '—' }}</td>
                <td class="px-3 py-2 text-right font-medium"
                    :class="row.stayDelta == null ? '' : row.stayDelta < 0 ? 'text-emerald-600' : 'text-rose-600'">
                  {{ row.stayDelta != null ? fmtDollar(row.stayDelta) : '—' }}
                </td>
                <td class="px-3 py-2 text-right text-blue-600">{{ row.joinPts || '—' }}</td>
                <td class="px-3 py-2 text-right">{{ row.joinCost ? fmtDollar(row.joinCost) : '—' }}</td>
                <td class="px-3 py-2 text-right font-medium"
                    :class="row.netSpend < 0 ? 'text-emerald-600' : 'text-rose-600'">
                  {{ fmtDollarM(row.netSpend) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Patient detail table -->
      <div v-if="selectedTin && loadingPatients" class="px-4 pb-4 py-6 text-center">
        <svg class="animate-spin h-5 w-5 text-gray-400 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
        </svg>
        <div class="text-xs text-gray-400 mt-2">Loading patients...</div>
      </div>
      <div v-else-if="selectedTin && patients.length" class="px-4 pb-4">
        <div class="flex items-center justify-between mb-2">
          <div class="text-xs font-semibold text-gray-700">
            {{ practiceNames[selectedTin] || selectedTin }} — {{ patients.length }} patients
            <span v-if="selectedCategory" class="text-gray-500">({{ selectedCategory }})</span>
          </div>
          <div class="flex items-center gap-2">
            <select v-model="selectedCategory" class="text-xs border border-gray-300 rounded px-2 py-1" @change="fetchPatients">
              <option :value="null">All Categories</option>
              <option value="Leavers">Leavers</option>
              <option value="Stayers">Stayers</option>
              <option value="Joiners">Joiners</option>
            </select>
            <button @click="selectedTin = null; patients = [];"
              class="text-xs text-gray-400 hover:text-gray-600">&times; Close</button>
          </div>
        </div>
        <div class="max-h-[300px] overflow-y-auto border border-gray-200 rounded">
          <table class="w-full text-xs">
            <thead class="bg-gray-50 sticky top-0">
              <tr>
                <th class="text-left px-3 py-2 font-medium text-gray-600">Patient</th>
                <th class="text-center px-3 py-2 font-medium text-gray-600">Category</th>
                <th class="text-right px-3 py-2 font-medium text-gray-600">Total Cost</th>
                <th class="text-right px-3 py-2 font-medium text-gray-600">IP</th>
                <th class="text-right px-3 py-2 font-medium text-gray-600">SNF</th>
                <th class="text-right px-3 py-2 font-medium text-gray-600">OP</th>
                <th class="text-right px-3 py-2 font-medium text-gray-600">Part B</th>
                <th class="text-right px-3 py-2 font-medium text-gray-600">Hospice</th>
                <th class="text-right px-3 py-2 font-medium text-gray-600">PY</th>
                <th class="text-right px-3 py-2 font-medium text-gray-600">HCC</th>
                <th class="text-center px-3 py-2 font-medium text-gray-600">Deceased</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="pt in patients" :key="pt.BENE_MBI_ID"
                  class="border-t border-gray-100 hover:bg-gray-50"
                  :class="pt.CATEGORY === 'Leavers' ? 'text-rose-700' : pt.CATEGORY === 'Joiners' ? 'text-blue-700' : ''">
                <td class="px-3 py-1.5">{{ pt.BENE_LAST_NAME }}, {{ pt.BENE_FIRST_NAME }}</td>
                <td class="px-3 py-1.5 text-center">{{ pt.CATEGORY }}</td>
                <td class="px-3 py-1.5 text-right font-mono">{{ fmtDollar(pt.TOT_COST) }}</td>
                <td class="px-3 py-1.5 text-right font-mono">{{ fmtDollar(pt.IP_COST) }}</td>
                <td class="px-3 py-1.5 text-right font-mono">{{ fmtDollar(pt.SNF_COST) }}</td>
                <td class="px-3 py-1.5 text-right font-mono">{{ fmtDollar(pt.OP_COST) }}</td>
                <td class="px-3 py-1.5 text-right font-mono">{{ fmtDollar(pt.PB_COST) }}</td>
                <td class="px-3 py-1.5 text-right font-mono">{{ fmtDollar(pt.HOSPICE_COST) }}</td>
                <td class="px-3 py-1.5 text-right">{{ pt.PERSON_YEARS?.toFixed(2) || '—' }}</td>
                <td class="px-3 py-1.5 text-right">{{ pt.HCC_SCORE?.toFixed(2) || '—' }}</td>
                <td class="px-3 py-1.5 text-center">{{ pt.IS_DECEASED ? '✝' : '' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

    </template>

    <div v-else class="p-8 text-center text-gray-400 text-sm">No data available</div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'CostAttributionFlow',
  props: {
    metricId: { type: Number, default: null },
    entityId: { type: Number, default: null },
    year: { type: Number, default: null },
    metricLinks: { type: Array, default: null },
    pageType: { type: String, default: null },
  },
  data() {
    return {
      loading: false,
      allResults: [],
      reportPairs: [],
      selectedDateA: null,
      selectedDateB: null,
      filteredResults: [],
      practiceNames: {},
      sortKey: 'name',
      sortAsc: true,
      riskAdjusted: false,
      groupBy: 'tin',
      selectedTin: null,
      selectedCategory: null,
      patients: [],
      loadingPatients: false,
      availableContracts: [],
      selectedContractId: null,
    };
  },
  computed: {
    dateAOptions() {
      // All A dates that have at least one B > A (i.e. any A that appears in a pair)
      const set = new Set(this.reportPairs.map(p => p.REPORT_DT_A));
      return [...set].sort();
    },
    dateBOptions() {
      // B dates must be strictly after the selected A
      if (!this.selectedDateA) {
        const set = new Set(this.reportPairs.map(p => p.REPORT_DT_B));
        return [...set].sort();
      }
      const set = new Set(
        this.reportPairs
          .filter(p => p.REPORT_DT_A === this.selectedDateA)
          .map(p => p.REPORT_DT_B)
      );
      return [...set].sort();
    },
    panelRows() {
      return this.filteredResults.filter(r => r.PRIMARY_TIN === '_PANEL');
    },
    tinRows() {
      return this.filteredResults.filter(r => r.PRIMARY_TIN !== '_PANEL' && r.PRIMARY_TIN != null);
    },
    summary() {
      if (!this.panelRows.length) return null;
      const cats = {};
      for (const row of this.panelRows) {
        const ra = this.riskAdjusted;
        cats[row.CATEGORY] = {
          patients: row.PATIENT_COUNT || 0,
          py: row.TOTAL_PERSON_YEARS || 0,
          spend: ra ? (row.RISK_ADJ_TOTAL_WEIGHTED_SPEND || 0) : (row.TOTAL_WEIGHTED_SPEND || 0),
          costPerPY: ra ? (row.RISK_ADJ_TOTAL_COST_PER_PY || null) : (row.WEIGHTED_TOTAL_COST_PER_PY || null),
          risk: row.AVG_RISK_SCORE || null,
          deceased: row.DECEASED_COUNT || 0,
          priorCostPerPY: ra ? (row.PRIOR_RISK_ADJ_TOTAL_COST_PER_PY || null) : (row.PRIOR_WEIGHTED_TOTAL_COST_PER_PY || null),
          priorPY: row.PRIOR_TOTAL_PERSON_YEARS || null,
          priorRisk: row.PRIOR_AVG_RISK_SCORE || null,
        };
      }
      return cats;
    },
    panelA() {
      if (!this.summary) return {};
      const l = this.summary.Leavers || {};
      const s = this.summary.Stayers || {};
      // Panel A = leavers (at report_a cost) + stayers (at report_a cost)
      const lPY = l.py || 0;
      const sPriorPY = s.priorPY || 0;
      const py = lPY + sPriorPY;
      const lSpend = l.spend || 0;
      const sSpend = (s.priorCostPerPY || 0) * sPriorPY;
      // Panel A risk: PY-weighted avg of leavers risk + stayers prior risk
      const lRiskPY = (l.risk || 0) * lPY;
      const sRiskPY = (s.priorRisk || 0) * sPriorPY;
      const risk = py > 0 ? (lRiskPY + sRiskPY) / py : null;
      return { patients: (l.patients || 0) + (s.patients || 0), py, costPerPY: py > 0 ? (lSpend + sSpend) / py : 0, risk };
    },
    panelB() {
      if (!this.summary) return {};
      const j = this.summary.Joiners || { patients: 0, py: 0, spend: 0 };
      const s = this.summary.Stayers || { patients: 0, py: 0, spend: 0 };
      const py = (j.py || 0) + (s.py || 0);
      const spend = (j.spend || 0) + (s.spend || 0);
      // Panel B risk: PY-weighted avg of joiners risk + stayers current risk
      const jRiskPY = (j.risk || 0) * (j.py || 0);
      const sRiskPY = (s.risk || 0) * (s.py || 0);
      const risk = py > 0 ? (jRiskPY + sRiskPY) / py : null;
      return { patients: (j.patients || 0) + (s.patients || 0), py, costPerPY: py > 0 ? spend / py : 0, risk };
    },
    costDelta() {
      return (this.panelB.costPerPY || 0) - (this.panelA.costPerPY || 0);
    },
    costDeltaPct() {
      return this.panelA.costPerPY ? this.costDelta / this.panelA.costPerPY : 0;
    },
    stayersPriorCost() {
      return this.summary?.Stayers?.priorCostPerPY || 0;
    },
    stayersCurrentCost() {
      return this.summary?.Stayers?.costPerPY || 0;
    },
    leaverEffect() {
      // Removing leavers: stayers-only at prior cost minus Panel A cost
      return this.stayersPriorCost - (this.panelA.costPerPY || 0);
    },
    stayerDrift() {
      return this.stayersCurrentCost - this.stayersPriorCost;
    },
    stayerDriftPct() {
      if (!this.stayersPriorCost) return 0;
      return (this.stayersCurrentCost - this.stayersPriorCost) / this.stayersPriorCost;
    },
    joinerEffect() {
      // Adding joiners: Panel B cost minus stayers-only at current cost
      return (this.panelB.costPerPY || 0) - this.stayersCurrentCost;
    },
    practices() {
      if (!this.tinRows.length) return [];
      const byTin = {};
      const ra = this.riskAdjusted;
      for (const row of this.tinRows) {
        const tin = row.PRIMARY_TIN || 'Unknown';
        if (!byTin[tin]) byTin[tin] = {
          tin, name: this.practiceNames[tin] || tin,
          // Leavers (report_a values)
          leftPts: 0, leftPY: 0, leftSpend: 0, leftRiskPY: 0, leftRiskDenom: 0,
          // Joiners (report_b values)
          joinPts: 0, joinPY: 0, joinSpend: 0, joinRiskPY: 0, joinRiskDenom: 0,
          // Stayers: prior = report_a, current = report_b
          stayPts: 0,
          stayPriorPY: 0, stayPriorSpend: 0, stayPriorRiskPY: 0, stayPriorRiskDenom: 0,
          stayCurrentPY: 0, stayCurrentSpend: 0, stayCurrentRiskPY: 0, stayCurrentRiskDenom: 0,
        };
        const b = byTin[tin];
        const spend = ra ? (row.RISK_ADJ_TOTAL_WEIGHTED_SPEND || 0) : (row.TOTAL_WEIGHTED_SPEND || 0);
        const py = row.TOTAL_PERSON_YEARS || 0;
        const risk = row.AVG_RISK_SCORE || 0;
        if (row.CATEGORY === 'Leavers') {
          b.leftPts += row.PATIENT_COUNT; b.leftPY += py; b.leftSpend += spend;
          if (risk) { b.leftRiskPY += risk * py; b.leftRiskDenom += py; }
        }
        if (row.CATEGORY === 'Joiners') {
          b.joinPts += row.PATIENT_COUNT; b.joinPY += py; b.joinSpend += spend;
          if (risk) { b.joinRiskPY += risk * py; b.joinRiskDenom += py; }
        }
        if (row.CATEGORY === 'Stayers') {
          b.stayPts += row.PATIENT_COUNT;
          b.stayCurrentPY += py; b.stayCurrentSpend += spend;
          if (risk) { b.stayCurrentRiskPY += risk * py; b.stayCurrentRiskDenom += py; }
          // Prior period
          const priorPY = row.PRIOR_TOTAL_PERSON_YEARS || 0;
          const priorSpend = ra ? ((row.PRIOR_RISK_ADJ_TOTAL_COST_PER_PY || 0) * priorPY) : ((row.PRIOR_WEIGHTED_TOTAL_COST_PER_PY || 0) * priorPY);
          const priorRisk = row.PRIOR_AVG_RISK_SCORE || 0;
          b.stayPriorPY += priorPY; b.stayPriorSpend += priorSpend;
          if (priorRisk) { b.stayPriorRiskPY += priorRisk * priorPY; b.stayPriorRiskDenom += priorPY; }
        }
      }
      const panelAvg = this.panelA.costPerPY || 0;
      return Object.values(byTin).map(b => {
        // Before = leavers + stayers at report_a
        const beforePY = b.leftPY + b.stayPriorPY;
        const beforeSpend = b.leftSpend + b.stayPriorSpend;
        const beforeCost = beforePY > 0 ? beforeSpend / beforePY : null;
        const beforeRiskDenom = b.leftRiskDenom + b.stayPriorRiskDenom;
        const beforeRisk = beforeRiskDenom > 0 ? (b.leftRiskPY + b.stayPriorRiskPY) / beforeRiskDenom : null;
        // After = joiners + stayers at report_b
        const afterPY = b.joinPY + b.stayCurrentPY;
        const afterSpend = b.joinSpend + b.stayCurrentSpend;
        const afterCost = afterPY > 0 ? afterSpend / afterPY : null;
        const afterRiskDenom = b.joinRiskDenom + b.stayCurrentRiskDenom;
        const afterRisk = afterRiskDenom > 0 ? (b.joinRiskPY + b.stayCurrentRiskPY) / afterRiskDenom : null;
        // Benchmark impact: what did this practice's churn do to the ACO per-capita
        const leftCost = b.leftPY > 0 ? b.leftSpend / b.leftPY : null;
        const joinCost = b.joinPY > 0 ? b.joinSpend / b.joinPY : null;
        const leaverImpact = leftCost != null ? (panelAvg - leftCost) * b.leftPY : 0;
        const joinerImpact = joinCost != null ? (joinCost - panelAvg) * b.joinPY : 0;
        const stayBeforeCost = b.stayPriorPY > 0 ? b.stayPriorSpend / b.stayPriorPY : null;
        const stayAfterCost = b.stayCurrentPY > 0 ? b.stayCurrentSpend / b.stayCurrentPY : null;
        const stayDelta = stayBeforeCost != null && stayAfterCost != null ? stayAfterCost - stayBeforeCost : null;
        const stayDeltaPct = stayBeforeCost ? (stayAfterCost - stayBeforeCost) / stayBeforeCost : null;
        return {
          ...b,
          leftCost, joinCost,
          stayCost: stayAfterCost,
          stayBeforeCost, stayAfterCost, stayDelta, stayDeltaPct,
          beforePts: b.leftPts + b.stayPts,
          beforeCost, beforeRisk,
          afterPts: b.joinPts + b.stayPts,
          afterCost, afterRisk,
          netSpend: leaverImpact + joinerImpact,
        };
      });
    },
    groupByLabel() {
      return { tin: 'By Practice (TIN)', facility: 'By Facility', staff: 'By Staff (NPI)' }[this.groupBy] || 'By Practice (TIN)';
    },
    groupByColumnLabel() {
      return { tin: 'Practice', facility: 'Facility', staff: 'Provider' }[this.groupBy] || 'Practice';
    },
    sortedPractices() {
      const key = this.sortKey;
      const dir = this.sortAsc ? 1 : -1;
      if (key === 'name') {
        return [...this.practices].sort((a, b) => (a.name || '').localeCompare(b.name || '') * dir);
      }
      return [...this.practices].sort((a, b) => ((a[key] || 0) - (b[key] || 0)) * dir);
    },
  },
  methods: {
    async fetchData() {
      this.loading = true;

      // On first load, discover which contracts have overlapping BEUR+ATTRIB data
      if (!this.availableContracts.length) {
        try {
          const discoResp = await axios.post('api/portfolio_management/cost-attribution-flow/contracts/');
          this.availableContracts = discoResp.data?.contracts || [];
        } catch { /* ignore */ }
      }

      // Pick contract: explicit selection > metricLinks > first available
      let contractId = this.selectedContractId;
      if (!contractId) {
        const fromLinks = this.metricLinks?.map(l => l.contract_id).filter(Boolean) || [];
        contractId = fromLinks.find(id => this.availableContracts.some(c => c.contract_id === id))
          || this.availableContracts[0]?.contract_id
          || null;
        this.selectedContractId = contractId;
      }

      if (!contractId) {
        this.loading = false;
        return;
      }

      const payload = { group_by: this.groupBy, contract_id: contractId };
      if (this.entityId) payload.entity_id = this.entityId;
      if (this.selectedDateA) payload.report_dt_a = this.selectedDateA;
      if (this.selectedDateB) payload.report_dt_b = this.selectedDateB;

      try {
        const resp = await axios.post('api/portfolio_management/cost-attribution-flow/', payload);
        this.allResults = resp.data.results || [];
        this.filteredResults = this.allResults;
        this.practiceNames = resp.data.tin_names || {};
        // Only refresh reportPairs if we don't have any yet — avoids churn on date changes
        if (!this.reportPairs.length) {
          this.reportPairs = (resp.data.report_pairs || []).map(p => ({
            ...p,
            key: `${p.REPORT_DT_A}|${p.REPORT_DT_B}`,
            label: `${p.REPORT_DT_A} → ${p.REPORT_DT_B}`,
          }));
          // Default to widest span (earliest A, latest B) on first load
          if (this.reportPairs.length && (!this.selectedDateA || !this.selectedDateB)) {
            const widest = this.reportPairs[this.reportPairs.length - 1];
            this.selectedDateA = widest.REPORT_DT_A;
            this.selectedDateB = widest.REPORT_DT_B;
            // Re-fetch now that dates are set
            await this.fetchData();
          }
        }
      } catch (err) {
        console.error('CostAttributionFlow error:', err);
      } finally {
        this.loading = false;
      }
    },
    filterData() {
      // Date picker changed — re-fetch from server for the new pair
      this.fetchData();
    },
    onDateAChange() {
      // If current B is not compatible with new A, pick the latest compatible B
      const validBs = this.reportPairs
        .filter(p => p.REPORT_DT_A === this.selectedDateA)
        .map(p => p.REPORT_DT_B);
      if (!validBs.includes(this.selectedDateB)) {
        this.selectedDateB = validBs[validBs.length - 1] || null;
      }
      this.selectedTin = null;
      this.patients = [];
      this.filterData();
    },
    onDateBChange() {
      // If current A is not compatible with new B, pick the earliest compatible A
      const validAs = this.reportPairs
        .filter(p => p.REPORT_DT_B === this.selectedDateB)
        .map(p => p.REPORT_DT_A);
      if (!validAs.includes(this.selectedDateA)) {
        this.selectedDateA = validAs[0] || null;
      }
      this.selectedTin = null;
      this.patients = [];
      this.filterData();
    },
    
    selectPractice(row) {
      if (this.selectedTin === row.tin) {
        this.selectedTin = null;
        this.patients = [];
        return;
      }
      this.selectedTin = row.tin;
      this.selectedCategory = null;
      this.patients = [];
      this.fetchPatients();
    },
    onContractChange() {
      // Reset transient state and reload for the new contract
      this.allResults = [];
      this.reportPairs = [];
      this.selectedDateA = null;
      this.selectedDateB = null;
      this.filteredResults = [];
      this.selectedTin = null;
      this.selectedCategory = null;
      this.patients = [];
      this.fetchData();
    },
    async fetchPatients() {
      if (!this.selectedTin || !this.selectedDateA || !this.selectedDateB) return;
      this.loadingPatients = true;
      const a = this.selectedDateA;
      const b = this.selectedDateB;
      const contractId = this.selectedContractId;
      if (!contractId) { this.loadingPatients = false; return; }
      try {
        const resp = await axios.post('api/portfolio_management/cost-attribution-flow/patients/', {
          contract_id: contractId,
          report_dt_a: a,
          report_dt_b: b,
          group_key: this.selectedTin,
          group_by: this.groupBy,
          category: this.selectedCategory || undefined,
        });
        this.patients = resp.data.patients || [];
      } catch (err) {
        console.error('fetchPatients error:', err);
        this.patients = [];
      } finally {
        this.loadingPatients = false;
      }
    },
    sortBy(key) {
      if (this.sortKey === key) this.sortAsc = !this.sortAsc;
      else { this.sortKey = key; this.sortAsc = key === 'name'; }
    },
    fmt(n) { return n != null ? n.toLocaleString() : '—'; },
    fmtPY(n) { return n != null ? n.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '—'; },
    fmtDollar(n) {
      if (n == null) return '—';
      const abs = Math.abs(n);
      const prefix = n < 0 ? '-' : '';
      return prefix + '$' + abs.toLocaleString(undefined, { maximumFractionDigits: 0 });
    },
    fmtDollarM(n) {
      if (n == null) return '—';
      const m = n / 1e6;
      const prefix = m < 0 ? '-' : '+';
      return prefix + '$' + Math.abs(m).toFixed(1) + 'M';
    },
    fmtPct(n) {
      if (n == null) return '—';
      return (n >= 0 ? '+' : '') + (n * 100).toFixed(1) + '%';
    },
  },
  mounted() {
    this.fetchData();
  },
  watch: {
    entityId() { this.fetchData(); },
  },
};
</script>
